import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, StrictMode, Suspense, type ComponentType, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router';
import { Layout } from './components/Layout';
import { AppearanceProvider } from './lib/appearance';
import { AuthProvider, useAuth } from './lib/auth';
import { Login, Register, Waitlist } from './pages/Auth';
import { CreateLeague, Dashboard, InboxPage, JoinLeague, MyLeagues } from './pages/Owner';
import { Loading, ToastProvider } from './components/kit';
import { Landing } from './pages/Landing';
import { NotFound } from './pages/Misc';

/**
 * Route-level code splitting: the league suite, Help, Welcome and the social pages load on
 * first visit (esbuild emits each as its own chunk).
 */
function lazyPage<M extends Record<string, unknown>>(
  load: () => Promise<M>,
  name: keyof M & string,
) {
  const Page = lazy(() => load().then((m) => ({ default: m[name] as ComponentType })));
  return (
    <Suspense fallback={<Loading label="Opening the boardroom" />}>
      <Page />
    </Suspense>
  );
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') {
    return (
      <p role="status" className="p-10 text-center text-slate">
        Opening the boardroom…
      </p>
    );
  }
  if (status === 'signed_out')
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

const admin = (name: string) => lazyPage(() => import('./pages/admin'), name as 'AdminOverview');

const router = createBrowserRouter([
  {
    // The admin has its own shell (header + sidebar). Its code is a separate lazy chunk; loading
    // it grants nothing — every /api/admin call is authorized on the server.
    path: '/admin',
    element: <RequireAuth>{lazyPage(() => import('./pages/admin'), 'AdminShell')}</RequireAuth>,
    children: [
      { index: true, element: admin('AdminOverview') },
      { path: 'users', element: admin('AdminUsers') },
      { path: 'users/:id', element: admin('AdminUserDetail') },
      { path: 'leagues', element: admin('AdminLeagues') },
      { path: 'leagues/:id', element: admin('AdminLeagueDetail') },
      { path: 'modes', element: admin('AdminModes') },
      { path: 'franchises', element: admin('AdminSports') },
      { path: 'activity', element: admin('AdminActivity') },
      { path: 'retention', element: admin('AdminRetention') },
      { path: 'finance', element: admin('AdminFinance') },
      { path: 'feedback', element: admin('AdminFeedback') },
      { path: 'feedback/:id', element: admin('AdminFeedbackDetail') },
      { path: 'waitlist', element: admin('AdminWaitlist') },
      { path: 'notifications', element: admin('AdminNotifications') },
      { path: 'system', element: admin('AdminSystem') },
      { path: 'audit', element: admin('AdminAudit') },
      { path: 'settings', element: admin('AdminSettings') },
      { path: 'admins', element: admin('AdminAdmins') },
      { path: '*', element: <NotFound /> },
    ],
  },
  {
    element: <Layout />,
    children: [
      { path: '/', element: <Landing /> },
      { path: '/login', element: <Login /> },
      { path: '/register', element: <Register /> },
      { path: '/waitlist', element: <Waitlist /> },
      { path: '/privacy', element: lazyPage(() => import('./pages/Public'), 'PrivacyPage') },
      { path: '/terms', element: lazyPage(() => import('./pages/Public'), 'TermsPage') },
      {
        path: '/forgot-password',
        element: lazyPage(() => import('./pages/Public'), 'ForgotPasswordPage'),
      },
      {
        path: '/reset-password',
        element: lazyPage(() => import('./pages/Public'), 'ResetPasswordPage'),
      },
      {
        path: '/verify-email',
        element: lazyPage(() => import('./pages/Public'), 'VerifyEmailPage'),
      },
      {
        path: '/feedback',
        element: (
          <RequireAuth>{lazyPage(() => import('./pages/Public'), 'FeedbackPage')}</RequireAuth>
        ),
      },
      { path: '/s/:token', element: lazyPage(() => import('./pages/Social'), 'SharePage') },
      { path: '/help', element: lazyPage(() => import('./pages/Help'), 'HelpCenter') },
      {
        path: '/help/:articleId',
        element: lazyPage(() => import('./pages/Help'), 'HelpArticlePage'),
      },
      {
        path: '/welcome',
        element: <RequireAuth>{lazyPage(() => import('./pages/Welcome'), 'Welcome')}</RequireAuth>,
      },
      {
        path: '/settings',
        element: (
          <RequireAuth>{lazyPage(() => import('./pages/Social'), 'SettingsPage')}</RequireAuth>
        ),
      },
      {
        path: '/achievements',
        element: (
          <RequireAuth>{lazyPage(() => import('./pages/Social'), 'AchievementsPage')}</RequireAuth>
        ),
      },
      {
        path: '/dashboard',
        element: (
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        ),
      },
      {
        path: '/leagues',
        element: (
          <RequireAuth>
            <MyLeagues />
          </RequireAuth>
        ),
      },
      {
        path: '/leagues/new',
        element: (
          <RequireAuth>
            <CreateLeague />
          </RequireAuth>
        ),
      },
      {
        path: '/join',
        element: (
          <RequireAuth>
            <JoinLeague />
          </RequireAuth>
        ),
      },
      {
        path: '/inbox',
        element: (
          <RequireAuth>
            <InboxPage />
          </RequireAuth>
        ),
      },
      {
        path: '/leagues/:leagueId',
        element: (
          <RequireAuth>
            {lazyPage(() => import('./pages/league/LeagueShell'), 'LeagueShell')}
          </RequireAuth>
        ),
        children: [
          {
            index: true,
            element: lazyPage(() => import('./pages/league/Headquarters'), 'Headquarters'),
          },
          { path: 'roster', element: lazyPage(() => import('./pages/league/Players'), 'Roster') },
          {
            path: 'free-agency',
            element: lazyPage(() => import('./pages/league/Players'), 'FreeAgency'),
          },
          { path: 'trades', element: lazyPage(() => import('./pages/league/Trades'), 'Trades') },
          {
            path: 'transfers',
            element: lazyPage(() => import('./pages/league/Transfers'), 'Transfers'),
          },
          {
            path: 'finances',
            element: lazyPage(() => import('./pages/league/Business'), 'Finances'),
          },
          { path: 'venue', element: lazyPage(() => import('./pages/league/Business'), 'Venue') },
          {
            path: 'sponsors',
            element: lazyPage(() => import('./pages/league/Business'), 'Sponsors'),
          },
          { path: 'staff', element: lazyPage(() => import('./pages/league/Business'), 'Staff') },
          {
            path: 'standings',
            element: lazyPage(() => import('./pages/league/LeagueInfo'), 'Standings'),
          },
          {
            path: 'results',
            element: lazyPage(() => import('./pages/league/LeagueInfo'), 'Results'),
          },
          {
            path: 'activity',
            element: lazyPage(() => import('./pages/league/LeagueInfo'), 'Activity'),
          },
          {
            path: 'inbox',
            element: lazyPage(() => import('./pages/league/LeagueInfo'), 'LeagueInbox'),
          },
          { path: 'rules', element: lazyPage(() => import('./pages/league/LeagueInfo'), 'Rules') },
          {
            path: 'commissioner',
            element: lazyPage(() => import('./pages/league/LeagueInfo'), 'Commissioner'),
          },
          { path: 'desk', element: lazyPage(() => import('./pages/league/Desk'), 'Desk') },
          {
            path: 'desk/:sessionId',
            element: lazyPage(() => import('./pages/league/Desk'), 'NegotiationRoom'),
          },
          { path: 'daily', element: lazyPage(() => import('./pages/league/Daily'), 'Daily') },
          {
            path: 'daily/:slug',
            element: lazyPage(() => import('./pages/league/Daily'), 'ArticlePage'),
          },
          {
            path: 'advisor',
            element: lazyPage(() => import('./pages/league/Daily'), 'AdvisorPage'),
          },
          { path: 'draft', element: lazyPage(() => import('./pages/league/Draft'), 'DraftRoom') },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppearanceProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </AppearanceProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
